import { ReuseModal } from "../../ReuseModal";
import { useEffect, useState } from "react";
import { MismatchAccordion } from "./HierarchicalGroupWrapper";
import {
  campaignsApi,
  useResolveMismatchesMutation,
  accountsApi,
} from "../../../../api";
import {
  AccountsMismatch,
  ContactsMismatch,
  MismatchAccount,
} from "../../../../types/apiTypes";
import { useAppDispatch } from "../../../../hooks";
import { Stack } from "@mui/material";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";

type Mismatches = {
  accounts: AccountsMismatch<MismatchAccount>;
  contacts: ContactsMismatch[];
};

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onClose: () => void;
  crm_mismatches: Mismatches;
  platform_mismatches: Mismatches;
  campaign_id: string;
};

export const MismatchDataModal = ({
  isOpen,
  setIsOpen,
  crm_mismatches,
  platform_mismatches,
  campaign_id,
  onClose,
}: Props) => {
  const dispatch = useAppDispatch();
  const [isResolving, setIsResolving] = useState<boolean>(false);
  const [selectedCRMList, setSelectedCRMList] = useState<
    Record<string, string[]>
  >({});
  const [selectedBCRList, setSelectedBCRList] = useState<
    Record<string, string[]>
  >({});

  const [expandAccordion, setExpandAccordion] = useState<string>("");

  const [resolveMismatches, { isLoading }] = useResolveMismatchesMutation();

  useEffect(() => {
    if (isLoading) setIsResolving(true);
  }, [isLoading]);

  const submitHandler = () => {
    resolveMismatches({
      campaign_id,
      crm_mismatches: {
        accounts: {
          created: selectedCRMList.created || [],
          updated: selectedCRMList.updated || [],
          deleted: selectedCRMList.deleted || [],
        },
        contacts: selectedCRMList.contacts || [],
      },
      platform_mismatches: {
        accounts: {
          created: selectedBCRList.created || [],
          updated: selectedBCRList.updated || [],
          deleted: selectedBCRList.deleted || [],
        },
        contacts: selectedBCRList.contacts || [],
      },
    })
      .unwrap()
      .then(() => {
        onClose();

        dispatch(
          accountsApi.util.invalidateTags([
            "analytics",
            "export-view",
            "list-view",
          ]),
        );

        dispatch(
          campaignsApi.util.invalidateTags([
            "engagement-config",
            "members",
            "company",
          ]),
        );
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title="Mismatch Failed"
          />,
        );
        setIsResolving(false);
      });
  };

  return (
    <ReuseModal
      isOpen={isOpen}
      onClose={isResolving ? onClose : () => setIsOpen(false)}
      title="Re-Sync Data / Data Mismatch"
      maxWidth="540px"
      submitBtnText="Re-Sync"
      submitBtnColor="#1C3C6C"
      onSubmit={submitHandler}
      cancelBtnText="Back"
      isLoading={isResolving}
      isDisabled={
        isResolving ||
        (Object.values(selectedCRMList).every((el) => el.length === 0) &&
          Object.values(selectedBCRList).every((el) => el.length === 0))
      }
      isDisabledCancelBtn={isResolving}
    >
      <Stack gap="8px">
        <MismatchAccordion
          title="BCR Changes"
          contacts={platform_mismatches.contacts}
          accounts={platform_mismatches.accounts}
          setSelectedList={setSelectedBCRList}
          expandAccordion={expandAccordion}
          setExpandAccordion={setExpandAccordion}
        />
        <MismatchAccordion
          title="CRM Changes"
          contacts={crm_mismatches.contacts}
          accounts={crm_mismatches.accounts}
          setSelectedList={setSelectedCRMList}
          expandAccordion={expandAccordion}
          setExpandAccordion={setExpandAccordion}
        />
      </Stack>
    </ReuseModal>
  );
};
